import * as amplitude from "@amplitude/analytics-react-native"
import { Ability } from "@casl/ability"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import { PlaySoundM } from "@common/components/playSound/PlaySoundM"
import { useGTMWeb, usePreventTranslation } from "@common/hooks/useGTMWeb"
import LKEvents from "@common/livekit/components-web/LKEvents"
import LiveKitRoomM from "@common/livekit/LiveKitRoomM"
import { navigationScreens } from "@common/navigation/navigationScreens"
import {
    classifyDeepLink,
    isAuthOnlyPath,
    menuScreens,
} from "@common/utils/navigationUtils"
import RootNavigator from "@common/navigation/RootNavigator"
import { liveAwareLinking } from "@common/navigation/liveAddress"
import { ErrorBoundaryFallbackView } from "@common/screens/ErrorBoundaryFallbackView"
import { theme } from "@common/theme/theme"
import { LaunchSplash } from "@common/screens/LaunchSplash"
import { useForegroundUpdate } from "@common/updates/useForegroundUpdate"
import { useLaunchUpdate } from "@common/updates/useLaunchUpdate"
import { runningBundle } from "@common/updates/runningBundle"
// Side-effect import: captures the Meta click id from the URL at module load,
// before React mounts. The address bar is rewritten to "/" further down this
// file, so any later read would come too late.
import "@common/utils/adClickCapture"
import {
    initBugsnag,
    initErrorBoundary,
    notifyBugsnag,
    setBugsnagUser,
} from "@common/utils/initBugsnag"
import {
    DimensionsContext,
    IS_WEB,
    NavigationRefContext,
} from "@common/utils/mobileUtils"
import {
    SystemBarsFrame,
    useWindowHeight,
} from "@common/systemBars/SystemBarsFrame"
import { setupCallService } from "./src/engine/live/room/callKeep/CallKeepSetup"
import { API_URL, APP_URL } from "@data/constants/apiConstants"
import { CLIENT_FEATURES, CLIENT_FEATURES_HEADER } from "@data/clientFeatures"
import { setPendingDeepLink } from "@data/redux/actions/notifications"
import { persistor, store } from "@data/redux/storeConfig/store"
import { openLiveKey } from "@data/redux/reducers/liveMode/liveMemory"
import { abilitiesForRoles } from "@data/utility/ability"
import { AbilityContext } from "@data/utility/Can"
import { initialAbility } from "@data/utility/initialAbility"
import { networkFailuresOwned } from "@data/utility/networkFailureOwner"
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import NetInfo from "@react-native-community/netinfo"
import { useReduxDevToolsExtension } from "@react-navigation/devtools"
import {
    getPathFromState as defaultGetPathFromState,
    getStateFromPath as defaultGetStateFromPath,
    NavigationContainer,
    useNavigationContainerRef,
} from "@react-navigation/native"
import axios from "axios"
import Constants from "expo-constants"
import "expo-dev-client"
import { useFonts } from "expo-font"
import * as Linking from "expo-linking"
import * as Notifications from "expo-notifications"
import React, { useEffect, useRef, useState } from "react"
import { Dimensions } from "react-native"
import "react-native-gesture-handler"
import { GestureHandlerRootView } from "react-native-gesture-handler"
import { Provider as PaperProvider } from "react-native-paper"
import { fr, registerTranslation } from "react-native-paper-dates"
import { MenuProvider } from "react-native-popup-menu"
import { SafeAreaProvider } from "react-native-safe-area-context"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { Provider as StoreProvider } from "react-redux"
import { PersistGate } from "redux-persist/lib/integration/react"
import { TourGuideProvider } from "rn-tourguide"
import { captureLoginLinkToken } from "@data/auth/loginLinkToken"
import { ToastProvider } from "./src/common/components/card/Toast"
import { dsFontAssets } from "./src/design-system/theme/fonts"
import { ChampionTooltipM } from "./src/student/champions/ChampionTooltipM"
import { version as appVersion } from "./package.json"
import { deviceTimezoneHeaders } from "./src/data/utility/timezones"

// Hold latest network snapshot to avoid per-request fetch
let latestNetworkInfo = null
NetInfo.addEventListener((state) => {
    latestNetworkInfo = {
        type: state.type,
        isConnected: state.isConnected,
        isInternetReachable: state.isInternetReachable,
    }
})

// Setup for CallService (keep alive in background)
setupCallService()

registerTranslation("fr", fr)

const prefix = Linking.createURL("/")

// Before Bugsnag starts: it reports the page URL.
captureLoginLinkToken()
initBugsnag()
const ErrorBoundary = !__DEV__ ? initErrorBoundary() : null

if (
    Constants.expoConfig.extra.amplitude?.apiKey &&
    JSON.stringify(Constants.expoConfig.extra.amplitude?.apiKey) !== "{}"
) {
    amplitude.init(Constants.expoConfig.extra.amplitude?.apiKey, null, {
        serverZone: amplitude.Types.ServerZone.EU,
    })
}

if (!IS_WEB)
    Notifications.setNotificationHandler({
        handleNotification: async () => ({
            shouldShowAlert: true,
            shouldPlaySound: true,
            shouldSetBadge: true,
        }),
    })

const window = Dimensions.get("window")
const screen = Dimensions.get("screen")

function App() {
    useGTMWeb() // google tag manager
    usePreventTranslation() // prevent translation

    const [dimensions, setDimensions] = useState({ window, screen })
    const height = useWindowHeight()
    const navigationRef = useNavigationContainerRef()
    const routeNameRef = useRef("")

    if (__DEV__) useReduxDevToolsExtension(navigationRef)

    // Classify a URL against current auth/mode state.
    const classify = (url) =>
        classifyDeepLink(url, {
            token: store.getState()?.data?.session?.object?.token,
            isParentMode: store.getState()?.data?.family?.isParentMode,
        })

    // Web only: handle deep-link URLs on first mount. Dispatch the pending
    // deep link to Redux ourselves and clean the address bar so a refresh
    // doesn't re-trigger the same flow. We can't rely on linking.getInitialURL
    // alone: PersistGate blocks NavigationContainer mount until rehydration
    // completes, so by the time getInitialURL is called Linking.getInitialURL
    // returns the rewritten "/" and dispatches nothing.
    useEffect(() => {
        if (!IS_WEB) return
        const pathname = globalThis.location?.pathname
        if (!pathname || pathname === "/") return

        const href = globalThis.location.href
        const decision = classify(href)

        if (decision.action === "external") {
            globalThis.location.replace(decision.url)
            return
        }

        if (decision.action === "pend") {
            store.dispatch(setPendingDeepLink(href))
        }

        const shouldRewrite =
            decision.action === "pend" ||
            (decision.action === "ignore" && isAuthOnlyPath(decision.path))
        if (shouldRewrite) globalThis.history.replaceState(null, "", "/")
    }, [])

    useEffect(() => {
        const subscription = Dimensions.addEventListener(
            "change",
            ({ window, screen }) => {
                setDimensions({ window, screen })
            }
        )
        return () => subscription?.remove()
    }, [])

    const [loaded] = useFonts({
        // Outfit — la typo du design system. Les écrans legacy restent en Montserrat.
        ...dsFontAssets,
        "Owl-Cute": require("@assets/fonts/Owl-Cute.ttf"),
        "Montserrat-Black": require("@assets/fonts/Montserrat-Black.ttf"),
        "Montserrat-BlackItalic": require("@assets/fonts/Montserrat-BlackItalic.ttf"),
        "Montserrat-Bold": require("@assets/fonts/Montserrat-Bold.ttf"),
        "Montserrat-BoldItalic": require("@assets/fonts/Montserrat-BoldItalic.ttf"),
        "Montserrat-ExtraBold": require("@assets/fonts/Montserrat-ExtraBold.ttf"),
        "Montserrat-ExtraBoldItalic": require("@assets/fonts/Montserrat-ExtraBoldItalic.ttf"),
        "Montserrat-ExtraLight": require("@assets/fonts/Montserrat-ExtraLight.ttf"),
        "Montserrat-ExtraLightItalic": require("@assets/fonts/Montserrat-ExtraLightItalic.ttf"),
        "Montserrat-Italic": require("@assets/fonts/Montserrat-Italic.ttf"),
        "Montserrat-Light": require("@assets/fonts/Montserrat-Light.ttf"),
        "Montserrat-LightItalic": require("@assets/fonts/Montserrat-LightItalic.ttf"),
        "Montserrat-Medium": require("@assets/fonts/Montserrat-Medium.ttf"),
        "Montserrat-MediumItalic": require("@assets/fonts/Montserrat-MediumItalic.ttf"),
        "Montserrat-Regular": require("@assets/fonts/Montserrat-Regular.ttf"),
        "Montserrat-SemiBold": require("@assets/fonts/Montserrat-SemiBold.ttf"),
        "Montserrat-SemiBoldItalic": require("@assets/fonts/Montserrat-SemiBoldItalic.ttf"),
        "Montserrat-Thin": require("@assets/fonts/Montserrat-Thin.ttf"),
        "Montserrat-ThinItalic": require("@assets/fonts/Montserrat-ThinItalic.ttf"),
        ...(IS_WEB
            ? {
                  // Only the FontAwesome families are imported directly from
                  // react-native-vector-icons across the app. @expo/vector-icons
                  // load their own fonts on web, and react-native-paper is
                  // configured to render FontAwesome5 (see PaperProvider
                  // `settings.icon` below), so eagerly loading the other
                  // vector-icon TTFs (Ionicons, MaterialIcons,
                  // MaterialCommunityIcons, Feather) only inflated web startup.
                  FontAwesome: require("react-native-vector-icons/Fonts/FontAwesome.ttf"),
                  FontAwesome5_Brands: require("react-native-vector-icons/Fonts/FontAwesome5_Brands.ttf"),
                  FontAwesome5_Regular: require("react-native-vector-icons/Fonts/FontAwesome5_Regular.ttf"),
                  FontAwesome5_Solid: require("react-native-vector-icons/Fonts/FontAwesome5_Solid.ttf"),
                  FontAwesome6_Brands: require("react-native-vector-icons/Fonts/FontAwesome6_Brands.ttf"),
                  FontAwesome6_Regular: require("react-native-vector-icons/Fonts/FontAwesome6_Regular.ttf"),
                  FontAwesome6_Solid: require("react-native-vector-icons/Fonts/FontAwesome6_Solid.ttf"),
              }
            : {}),
    })

    // La mise à jour à distance s'applique avant le premier écran, et au retour après 15 min
    // (src/common/updates/).
    const launchUpdateSettled = useLaunchUpdate()
    useForegroundUpdate(navigationRef)

    if ((!loaded || !launchUpdateSettled) && !IS_WEB) {
        return <LaunchSplash />
    }

    const token = store.getState()?.data?.session?.object?.token

    // ** Configure axios to make requests
    axios.defaults.baseURL = API_URL
    if (token)
        axios.defaults.headers.common["Authorization"] = "Bearer " + token
    axios.defaults.headers.common["X-App-Version"] = appVersion
    // Ce que ce bundle sait afficher (src/data/clientFeatures.js) : le backend s'y adapte.
    axios.defaults.headers.common[CLIENT_FEATURES_HEADER] = CLIENT_FEATURES

    // ** Axios monitoring for Bugsnag (non-verbose)
    axios.interceptors.request.use((config) => {
        // attach start time and latest known network state (non-blocking)
        config.metadata = {
            start: Date.now(),
            network: latestNetworkInfo,
        }
        Object.assign(
            config.headers,
            deviceTimezoneHeaders({
                url: config.url,
                apiUrl: API_URL,
                parentMode: store.getState()?.data?.family?.isParentMode,
            })
        )
        return config
    })

    // ** Helper to determine if error should be reported to Bugsnag
    const shouldReportToBugsnag = (error, config, meta) => {
        const status = error?.response?.status
        const networkInfo = config?.metadata?.network

        // Skip if no status (network error) and device is offline
        if (!status && networkInfo?.isConnected === false) {
            return false // Expected offline behavior
        }

        // 4xx errors that indicate bugs (not expected user behavior)
        const buggy4xxStatuses = [
            405, // Method Not Allowed - wrong HTTP method, coding error
            408, // Request Timeout - performance issue
            413, // Payload Too Large - missing client-side validation
            415, // Unsupported Media Type - wrong content-type, coding error
            419, // CSRF Token Mismatch - session/token handling bug
            429, // Too Many Requests - potential infinite loop or rate limit bug
        ]

        // Skip most 4xx client errors (validation, not found, unauthorized, etc.)
        // But report the ones that likely indicate bugs
        if (status >= 400 && status < 500) {
            if (buggy4xxStatuses.includes(status)) {
                return true // These 4xx errors likely indicate bugs
            }
            return false // Skip other 4xx errors (expected user/client errors)
        }

        // Optional: Skip specific URLs that fail frequently by design
        const url = config?.url || ""
        const skipUrls = [
            // Add URLs here that you want to exclude from reporting
            // Example: "/api/optional-feature",
        ]
        if (skipUrls.some((skipUrl) => url.includes(skipUrl))) {
            return false
        }

        // Report everything else:
        // - 5xx server errors (bugs)
        // - Network errors when device should be online (connectivity issues)
        // - Timeout errors
        // - Other unexpected errors
        return true
    }

    axios.interceptors.response.use(
        (response) => {
            if (response.config && response.config.metadata) {
                response.config.metadata.end = Date.now()
                response.config.metadata.duration =
                    response.config.metadata.end -
                    response.config.metadata.start
            }
            return response
        },
        (error) => {
            const config = error.config || {}
            if (config.metadata) {
                config.metadata.end = Date.now()
                config.metadata.duration =
                    config.metadata.end - config.metadata.start
            }

            const meta = {
                url: config.url,
                method: config.method,
                duration_ms: config.metadata?.duration,
                status: error?.response?.status,
                response_data: error?.response?.data
                    ? {
                          message: error?.response?.data?.message,
                          exception: error?.response?.data?.exception,
                      }
                    : null,
                network: config.metadata?.network,
            }

            console.log("API call error with meta:", meta)
            const errorMessage =
                error?.message +
                " || " +
                (error?.response?.data?.message ??
                    error?.response?.data ??
                    error)

            if (!__DEV__) {
                // Only report important errors to Bugsnag
                if (shouldReportToBugsnag(error, config, meta)) {
                    notifyBugsnag("API call error: " + errorMessage)
                }
            } else {
                console.error("API call error: " + errorMessage)
            }

            // Un écran du design system qui dit lui-même ses échecs réseau (le live, l'activité de
            // l'élève), ou une lecture de fond qui a son propre repli (`silentNetworkError`) : la
            // boîte ne s'ouvre pas par-dessus.
            if (
                !error?.response &&
                !networkFailuresOwned() &&
                !config.silentNetworkError
            ) {
                DialogM.show({
                    text1: "Problème de connexion réseau",
                    text2: "Impossible de contacter le serveur. Vérifie ta connexion ou réessaie plus tard.",
                    buttonText1: "OK",
                })
            }

            return Promise.reject(error)
        }
    )

    // ** init abilities
    const userData = store.getState()?.data?.currentUser?.object
    const currentRoles = userData ? userData.roles : null
    const ability = new Ability(
        abilitiesForRoles(currentRoles) || initialAbility
    )

    // update abilities when role changes
    store.subscribe(() => {
        const prevRoles = currentRoles
        const userData = store.getState()?.data?.currentUser?.object
        const newRoles = userData ? userData.roles : null

        if (prevRoles !== newRoles) {
            ability.update(abilitiesForRoles(newRoles))
        }
    })

    // Partagée par onReady (route de cold-load) et onStateChange (les
    // suivantes) : vivant dans onStateChange, elle laissait l'écran d'ARRIVÉE
    // muet — sur un funnel ouvert par URL, c'est le dénominateur de tout
    // calcul de drop.
    const trackScreenView = (routeName) => {
        if (!routeName) return

        // Lu à l'appel : onReady tire avant la réhydratation de PersistGate,
        // donc un userData capturé au rendu y est toujours nul.
        const userData = store.getState()?.data?.currentUser?.object

        if (userData) {
            const identifyObj = new amplitude.Identify()
            identifyObj.set("roles", userData?.roles)
            identifyObj.set("bundle", runningBundle())
            identifyObj.set(
                "subscription_state",
                userData?.subscription_state?.status
            )
            amplitude.identify(identifyObj)
            amplitude.setUserId(userData?.email)
            setBugsnagUser(userData)
        }

        amplitude.track("screen.viewed." + routeName)
    }

    // ** Linking configuration for deep linking
    const defaultRoutes = Object.values(navigationScreens)
        ?.reduce((pValue, cValue) => {
            return [...pValue, ...cValue]
        }, [])
        ?.reduce((pValue, cValue) => {
            return { ...pValue, ...{ [cValue?.name]: cValue?.name } }
        }, {})

    const config = {
        initialRouteName: "menu",
        screens: {
            ...defaultRoutes,
            menu: {
                screens: menuScreens,
            },
            "not-found": "*",
        },
    }

    const pathTranslations = {
        // Override the default getStateFromPath so /impersonate mounts as the
        // sole screen on the stack — no `menu` underneath. With the default
        // (initialRouteName: "menu"), opening /impersonate?code=… pre-mounts
        // ParentMainLayout/ParentSubscribeScreen on the persisted (stale)
        // identity. Their on-mount effects fire data requests with the old
        // token → 401, and the same calls re-fire after bootstrap lands —
        // wasted round-trips and a visible flash of the previous identity.
        getStateFromPath: (path, options) => {
            const cleanPath = (path ?? "").replace(/^\/+/, "").split(/[?#]/)[0]
            if (cleanPath === "impersonate") {
                const queryString = path.includes("?")
                    ? path.split("?")[1].split("#")[0]
                    : ""
                const params = Object.fromEntries(
                    new URLSearchParams(queryString).entries()
                )
                return {
                    index: 0,
                    routes: [{ name: "impersonate", params }],
                }
            }
            return defaultGetStateFromPath(path, options)
        },
        getPathFromState: defaultGetPathFromState,
    }

    const linking = {
        prefixes: [prefix, `${APP_URL}/`],
        config,
        // Au web, le live a son adresse : `/live/<clé de l'entrée>`, ses écrans sous elle.
        ...(IS_WEB
            ? liveAwareLinking(pathTranslations, () =>
                  openLiveKey(store.getState())
              )
            : pathTranslations),
        getInitialURL: async () => {
            const url = await Linking.getInitialURL()
            if (!url) return null

            const decision = classify(url)

            if (decision.action === "external") {
                Linking.openURL(decision.url).catch(notifyBugsnag)
                return null
            }
            if (decision.action === "pend") {
                store.dispatch(setPendingDeepLink(url))
                return null
            }
            // Auth-only while authed: drop the URL so RN falls back to the
            // initial route and the useEffect redirects to "/". Web-only paths
            // (e.g. /subscribe/return) also classify as "ignore" but must
            // route normally so their registered screen renders.
            if (decision.action === "ignore") {
                return isAuthOnlyPath(decision.path) ? null : url
            }
            return url
        },
        subscribe: (listener) => {
            const subscription = Linking.addEventListener("url", ({ url }) => {
                const decision = classify(url)
                if (decision.action === "external") {
                    Linking.openURL(decision.url).catch(notifyBugsnag)
                    return
                }
                if (decision.action === "pend") {
                    store.dispatch(setPendingDeepLink(url))
                    return
                }
                if (decision.action === "route") listener(url)
                // "ignore" is a no-op
            })
            return () => subscription.remove()
        },
    }

    const width =
        Dimensions.get("window").width > 800
            ? (dimensions?.window?.width * 2) / 3
            : dimensions?.window?.width

    return (
        <SafeAreaProvider>
            <StoreProvider store={store}>
                <PersistGate loading={null} persistor={persistor}>
                    <LiveKitRoomM>
                        <AbilityContext.Provider value={ability}>
                            <GestureHandlerRootView
                                style={{
                                    flex: 1,
                                }}
                            >
                                <MenuProvider
                                    customStyles={{
                                        backdrop: {
                                            backgroundColor: theme.colors.black,
                                            opacity: 0.5,
                                        },
                                    }}
                                >
                                    <NavigationRefContext.Provider
                                        value={{ navigationRef }}
                                    >
                                        <PaperProvider
                                            theme={theme}
                                            settings={{
                                                icon: (props) => {
                                                    // Map Material Design icon names to FontAwesome5
                                                    const materialToFA5 = {
                                                        close: "times",
                                                        pencil: "pen",
                                                        "calendar-blank":
                                                            "calendar",
                                                    }
                                                    return (
                                                        <FontAwesome5
                                                            solid
                                                            {...props}
                                                            name={
                                                                materialToFA5[
                                                                    props.name
                                                                ] || props.name
                                                            }
                                                        />
                                                    )
                                                },
                                            }}
                                        >
                                            <ToastProvider>
                                                <DimensionsContext.Provider
                                                    value={{
                                                        width,
                                                        height,
                                                    }}
                                                >
                                                    <NavigationContainer
                                                        linking={linking}
                                                        documentTitle={{
                                                            formatter: (
                                                                options,
                                                                route
                                                            ) =>
                                                                `${
                                                                    options?.headerTitle ??
                                                                    route?.name
                                                                } - Masteur` +
                                                                (Constants
                                                                    .expoConfig
                                                                    .extra
                                                                    .releaseStage ===
                                                                "production"
                                                                    ? ""
                                                                    : " (" +
                                                                      Constants
                                                                          .expoConfig
                                                                          .extra
                                                                          .releaseStage +
                                                                      ")"),
                                                        }}
                                                        ref={navigationRef}
                                                        onReady={() => {
                                                            const currentRouteName =
                                                                navigationRef.getCurrentRoute()
                                                                    ?.name

                                                            routeNameRef.current =
                                                                currentRouteName

                                                            trackScreenView(
                                                                currentRouteName
                                                            )
                                                        }}
                                                        onStateChange={async () => {
                                                            const previousRouteName =
                                                                routeNameRef.current
                                                            const currentRouteName =
                                                                navigationRef.getCurrentRoute()
                                                                    .name

                                                            if (
                                                                previousRouteName !==
                                                                currentRouteName
                                                            ) {
                                                                // Save the current route name for later comparison
                                                                routeNameRef.current =
                                                                    currentRouteName

                                                                trackScreenView(
                                                                    currentRouteName
                                                                )
                                                            }
                                                        }}
                                                        theme={theme}
                                                    >
                                                        <LKEvents />
                                                        {!IS_WEB ? (
                                                            <TourGuideProvider
                                                                borderRadius={
                                                                    16
                                                                }
                                                                tooltipComponent={
                                                                    ChampionTooltipM
                                                                }
                                                                preventOutsideInteraction
                                                                animationDuration={
                                                                    50
                                                                }
                                                                androidStatusBarVisible={
                                                                    false
                                                                }
                                                            >
                                                                <BottomSheetModalProvider>
                                                                    <RootNavigator />
                                                                </BottomSheetModalProvider>
                                                            </TourGuideProvider>
                                                        ) : (
                                                            <BottomSheetModalProvider>
                                                                <RootNavigator />
                                                            </BottomSheetModalProvider>
                                                        )}
                                                    </NavigationContainer>
                                                </DimensionsContext.Provider>
                                                <DialogM />
                                                <SnackBarM />
                                                <PlaySoundM />
                                            </ToastProvider>
                                        </PaperProvider>
                                    </NavigationRefContext.Provider>
                                </MenuProvider>
                            </GestureHandlerRootView>
                        </AbilityContext.Provider>
                    </LiveKitRoomM>
                </PersistGate>
            </StoreProvider>
        </SafeAreaProvider>
    )
}

// ** Error Boundary with Bugsnag
export default () => (
    <SystemBarsFrame>
        {!__DEV__ ? (
            <ErrorBoundary FallbackComponent={ErrorBoundaryFallbackView}>
                <App />
            </ErrorBoundary>
        ) : (
            <App />
        )}
    </SystemBarsFrame>
)
